/**
 * Voice session state — module singleton + `useSyncExternalStore` hook, the
 * `runStateStore` idiom (`src/hooks/useAsyncInvokeAdapter.ts`). A voice
 * session is NOT a text run: it never touches the assistant-ui thread
 * runtime. Live transcripts render in the `VoiceSessionBar` over the
 * composer; the persisted turns land in the thread via the conversation
 * query invalidation on `voice_stopped` (wired by the caller through
 * `onSessionEnded`).
 *
 * Lifecycle: idle → requesting_mic → connecting → active → (stopping) → idle,
 * with `error` reachable from every non-idle state. The server's
 * `voice_stopped` frame is the authoritative end signal; a local 5 s fallback
 * covers a dropped reply. `connection_closed` (socket death) tears down too —
 * no reconnect/resume in v1.
 */

import { useSyncExternalStore } from 'react';
import {
  getOrCreateConnection,
  VoiceStartError,
  type VoiceConnectionEvent,
} from '../utils/direct-run-connection';
import { activeFocusedRunIdStore } from '../hooks/useAsyncInvokeAdapter';
import { startMicCapture, type MicCapture } from './mic-capture';
import { createPcmPlayer, type PcmPlayer } from './pcm-player';
import { voiceStats } from './voice-debug';
import { setAudioKeepAliveWhileHidden } from '../utils/audio-theme';

const STOP_FALLBACK_MS = 5_000;
/** Poll cadence for the player-derived `agentSpeaking` flag. */
const SPEAKING_POLL_MS = 200;
/** Mic level is quantized so 25 fps RMS frames don't re-render the bar 25×/s. */
const MIC_LEVEL_STEP = 0.05;

export type VoiceSessionStatus =
  | 'idle'
  | 'requesting_mic'
  | 'connecting'
  | 'active'
  | 'stopping'
  | 'error';

export interface VoiceSessionError {
  /** Wire code (`VOICE_CREDENTIAL_MISSING`, `VOICE_DISABLED`, `BUSY`, …) or a
   *  local one (`MIC_DENIED`, `MIC_NOT_FOUND`, `MIC_UNAVAILABLE`, `TIMEOUT`,
   *  `CONNECTION_CLOSED`, `SESSION_ENDED`). */
  code: string;
  /** Server-provided message, when one arrived (preferred for
   *  `VOICE_DISABLED`, which has two backend meanings). */
  message?: string;
}

export interface VoiceSessionState {
  status: VoiceSessionStatus;
  conversationId?: string;
  agentId?: string;
  runId?: string;
  /** True for a typed-only session (no mic requested — `voice_text` turns
   *  in, spoken audio out). */
  micless?: boolean;
  /** Wall-clock ms when the session went active — drives the elapsed timer. */
  startedAt?: number;
  /** Live user transcript (cumulative text per backend schema). */
  userPartial: string;
  /** Live assistant transcript (accumulated deltas). */
  assistantPartial: string;
  lastFinalTurn?: { role: 'user' | 'assistant'; text: string };
  /** "Using <tool>…" while a tool call is in flight during the session. */
  toolActivity?: string;
  agentSpeaking: boolean;
  /** Quantized mic RMS (0..1) for the listening pulse. */
  micLevel: number;
  error?: VoiceSessionError;
}

export interface StartVoiceSessionOptions {
  conversationId: string;
  agentId: string;
  spaceId?: string;
  /** false = typed-only session: skip getUserMedia entirely (no permission
   *  prompt, no capture) — input rides `voice_text` frames, replies are still
   *  real provider speech. Default true. */
  withMic?: boolean;
  /** Called on every session end (any reason) — the composer invalidates the
   *  conversation query here so persisted voice turns render in the thread. */
  onSessionEnded?: (info: { conversationId: string; reason: string }) => void;
}

const IDLE_STATE: VoiceSessionState = {
  status: 'idle',
  userPartial: '',
  assistantPartial: '',
  agentSpeaking: false,
  micLevel: 0,
};

/** `voice_stopped` reasons that are expected and need no user-facing notice. */
const NORMAL_STOP_REASONS = new Set(['client_stop', 'client_disconnected', 'cancelled']);

const STOP_REASON_NOTICES: Record<string, string> = {
  idle_timeout: 'Voice session ended after a period of silence.',
  max_duration: 'Voice session reached its duration limit.',
  provider_error: 'The voice provider ended the session unexpectedly.',
  provider_closed: 'The voice provider closed the session.',
  runtime_shutdown: 'The agent runtime restarted — voice session ended.',
};

interface SessionResources {
  mic: MicCapture | null;
  player: PcmPlayer | null;
  unsubscribe: (() => void) | null;
  conn: ReturnType<typeof getOrCreateConnection> | null;
  speakingTimer: ReturnType<typeof setInterval> | null;
  stopFallbackTimer: ReturnType<typeof setTimeout> | null;
  onSessionEnded?: StartVoiceSessionOptions['onSessionEnded'];
  setFocusedRun: boolean;
}

export class VoiceSessionStore {
  private state: VoiceSessionState = IDLE_STATE;
  private listeners = new Set<() => void>();
  private resources: SessionResources = emptyResources();
  /** Monotonic token — a stale async continuation (mic promise resolving after
   *  the session was torn down) must not mutate the next session's state. */
  private sessionSeq = 0;

  getSnapshot = (): VoiceSessionState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private setState(patch: Partial<VoiceSessionState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  /** Surface an error WITHOUT starting anything — used by the mic button when
   *  availability is definitively `unavailable` (admin knows there's no xAI
   *  key), so we never prompt for mic permission for a session that can't
   *  start. */
  showError(code: string, message?: string): void {
    this.setState({ status: 'error', error: { code, message } });
  }

  dismissError(): void {
    if (this.state.status !== 'error') return;
    this.state = IDLE_STATE;
    for (const l of this.listeners) l();
  }

  async start(opts: StartVoiceSessionOptions): Promise<void> {
    if (this.state.status !== 'idle' && this.state.status !== 'error') return;
    const seq = ++this.sessionSeq;
    const withMic = opts.withMic !== false;
    this.resources = emptyResources();
    this.resources.onSessionEnded = opts.onSessionEnded;
    this.state = {
      ...IDLE_STATE,
      status: withMic ? 'requesting_mic' : 'connecting',
      conversationId: opts.conversationId,
      agentId: opts.agentId,
      micless: !withMic,
    };
    for (const l of this.listeners) l();

    // 1. Mic first — never open a provider session we can't feed. A typed-only
    //    session skips capture entirely (feeding happens via voice_text).
    if (withMic) {
      let mic: MicCapture;
      try {
        mic = await startMicCapture({
          onFrame: (chunkB64, rms) => this.handleMicFrame(seq, chunkB64, rms),
        });
      } catch (err) {
        if (seq !== this.sessionSeq) return;
        this.setState({ status: 'error', error: micError(err) });
        return;
      }
      if (seq !== this.sessionSeq) {
        mic.stop();
        return;
      }
      this.resources.mic = mic;
    }

    // 2. Socket + voice_start handshake. (Micless start is born in
    //    'connecting' — don't re-notify.)
    if (withMic) this.setState({ status: 'connecting' });
    const conn = getOrCreateConnection(opts.conversationId, {
      getSpaceId: () => opts.spaceId,
      agentId: opts.agentId,
    });
    this.resources.conn = conn;
    this.resources.unsubscribe = conn.onVoiceEvent((e) => this.handleVoiceEvent(seq, e));
    try {
      const started = await conn.startVoice();
      if (seq !== this.sessionSeq) return;
      // 3. Live. Playback graph is unlocked by the entry button's click
      //    gesture (`unlockAudioPlayback`) — required on Safari.
      voiceStats.reset();
      this.resources.player = createPcmPlayer(started.outputAudio?.rate || undefined);
      voiceStats.playerReady = !!this.resources.player;
      // A live session keeps speaking when the tab is backgrounded — exempt
      // the shared context from the hidden-tab suspension.
      setAudioKeepAliveWhileHidden(true);
      this.resources.speakingTimer = setInterval(() => this.pollSpeaking(seq), SPEAKING_POLL_MS);
      this.resources.setFocusedRun = true;
      activeFocusedRunIdStore.set(started.runId);
      this.setState({ status: 'active', runId: started.runId, startedAt: Date.now() });
    } catch (err) {
      if (seq !== this.sessionSeq) return;
      this.releaseResources();
      const e =
        err instanceof VoiceStartError
          ? { code: err.code, message: err.message }
          : { code: 'VOICE_PROVIDER_ERROR', message: err instanceof Error ? err.message : undefined };
      this.setState({ status: 'error', error: e });
    }
  }

  /** Typed user turn during an active session (`voice_text`) — the agent
   *  answers in speech. The server persists it and echoes a user
   *  `voice_transcript`, which lands in `lastFinalTurn` via the normal event
   *  path, so no optimistic state is needed here. */
  sendText(text: string): void {
    if (this.state.status !== 'active') return;
    const trimmed = text.trim();
    if (!trimmed) return;
    this.resources.conn?.sendVoiceText(trimmed);
  }

  /** Graceful end: ask the server; `voice_stopped` drives the shared teardown.
   *  A fallback timer forces local teardown if the reply never lands. */
  stop(): void {
    if (this.state.status !== 'active' && this.state.status !== 'connecting') return;
    const seq = this.sessionSeq;
    this.setState({ status: 'stopping' });
    this.resources.conn?.stopVoice();
    this.resources.stopFallbackTimer = setTimeout(() => {
      if (seq !== this.sessionSeq) return;
      this.endSession('client_stop');
    }, STOP_FALLBACK_MS);
  }

  private handleMicFrame(seq: number, chunkB64: string, rms: number): void {
    if (seq !== this.sessionSeq) return;
    if (this.state.status === 'active') {
      this.resources.conn?.sendVoiceAudio(chunkB64);
    }
    const level = Math.min(1, Math.round(rms / MIC_LEVEL_STEP) * MIC_LEVEL_STEP);
    if (level !== this.state.micLevel) this.setState({ micLevel: level });
  }

  private pollSpeaking(seq: number): void {
    if (seq !== this.sessionSeq) return;
    const speaking = this.resources.player?.isSpeaking() ?? false;
    if (speaking !== this.state.agentSpeaking) this.setState({ agentSpeaking: speaking });
  }

  private handleVoiceEvent(seq: number, e: VoiceConnectionEvent): void {
    if (seq !== this.sessionSeq) return;
    switch (e.type) {
      case 'voice_started':
        // Handled via the startVoice() promise; nothing extra here.
        return;
      case 'voice_audio':
        voiceStats.audioFramesIn++;
        if (this.resources.player) {
          voiceStats.chunksEnqueued++;
          this.resources.player.enqueue(e.chunk);
        }
        return;
      case 'voice_interrupted':
        // Barge-in: kill queued speech NOW.
        this.resources.player?.flush();
        return;
      case 'voice_stopped':
        this.endSession(e.reason);
        return;
      case 'voice_error':
        // Mid-session server error; a voice_stopped normally follows, but
        // don't rely on it.
        this.endSession('provider_error');
        return;
      case 'connection_closed':
        this.endSession('connection_closed');
        return;
      case 'voice_run_event':
        this.handleRunEvent(e.eventType, e.data);
        return;
    }
  }

  private handleRunEvent(eventType: string, data: Record<string, unknown>): void {
    switch (eventType) {
      case 'agent.run.voice_transcript_delta': {
        const role = data.role as 'user' | 'assistant' | undefined;
        if (role === 'user') {
          // User transcript is cumulative text, not a delta.
          this.setState({ userPartial: (data.text as string) ?? this.state.userPartial });
        } else if (role === 'assistant') {
          const chunk = (data.chunk as string) ?? (data.text as string) ?? '';
          if (chunk) this.setState({ assistantPartial: this.state.assistantPartial + chunk });
        }
        return;
      }
      case 'agent.run.voice_transcript': {
        const role = data.role as 'user' | 'assistant' | undefined;
        const text = (data.text as string) ?? '';
        if (!role) return;
        this.setState({
          lastFinalTurn: { role, text },
          ...(role === 'user' ? { userPartial: '' } : { assistantPartial: '' }),
        });
        return;
      }
      case 'agent.run.tool_call': {
        const name =
          (data.toolDisplayName as string) ?? (data.toolName as string) ?? 'a tool';
        this.setState({ toolActivity: `Using ${name}…` });
        return;
      }
      case 'agent.run.tool_result':
        this.setState({ toolActivity: undefined });
        return;
      // agent.run.voice_session_started/stopped: the wire frames are
      // authoritative — these fan-out mirrors are ignored here.
      default:
        return;
    }
  }

  /** Shared teardown for every end path (server stop, socket death, fallback). */
  private endSession(reason: string): void {
    const { conversationId } = this.state;
    const onSessionEnded = this.resources.onSessionEnded;
    this.sessionSeq++;
    this.releaseResources();

    if (reason === 'connection_closed') {
      this.setState({
        status: 'error',
        agentSpeaking: false,
        micLevel: 0,
        toolActivity: undefined,
        error: { code: 'CONNECTION_CLOSED', message: 'The connection dropped — voice session ended.' },
      });
    } else if (!NORMAL_STOP_REASONS.has(reason)) {
      this.setState({
        status: 'error',
        agentSpeaking: false,
        micLevel: 0,
        toolActivity: undefined,
        error: {
          code: 'SESSION_ENDED',
          message: STOP_REASON_NOTICES[reason] ?? `Voice session ended (${reason}).`,
        },
      });
    } else {
      this.state = IDLE_STATE;
      for (const l of this.listeners) l();
    }

    // Persisted voice turns are now in the conversation — let the caller
    // invalidate the messages query so the thread catches up.
    if (conversationId) onSessionEnded?.({ conversationId, reason });
  }

  private releaseResources(): void {
    const r = this.resources;
    setAudioKeepAliveWhileHidden(false);
    r.mic?.stop();
    r.player?.flush();
    r.unsubscribe?.();
    if (r.speakingTimer) clearInterval(r.speakingTimer);
    if (r.stopFallbackTimer) clearTimeout(r.stopFallbackTimer);
    if (r.setFocusedRun) activeFocusedRunIdStore.set(undefined);
    this.resources = emptyResources();
  }
}

function emptyResources(): SessionResources {
  return {
    mic: null,
    player: null,
    unsubscribe: null,
    conn: null,
    speakingTimer: null,
    stopFallbackTimer: null,
    setFocusedRun: false,
  };
}

function micError(err: unknown): VoiceSessionError {
  const name = err instanceof DOMException ? err.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return {
      code: 'MIC_DENIED',
      message: "Microphone access was denied — check your browser's permission settings.",
    };
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return { code: 'MIC_NOT_FOUND', message: 'No microphone found.' };
  }
  return {
    code: 'MIC_UNAVAILABLE',
    message: err instanceof Error ? err.message : 'Could not start the microphone.',
  };
}

export const voiceSessionStore = new VoiceSessionStore();

export function useVoiceSession(): VoiceSessionState {
  return useSyncExternalStore(voiceSessionStore.subscribe, voiceSessionStore.getSnapshot);
}
